import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Left from './components/left';
import styles from './index.module.scss';
import ChatInterface from './components/chatBox';
import { AssistantPanelType, ChatProvider, useChatContext } from '../context';
import { ConfigProvider, theme } from 'antd';
import zhCN from 'antd/es/locale/zh_CN';
import backgroundImage from '@/assets/images/bg@2x.png';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { getProcessDefinitionList } from '@/api/chat';
import type { ProcessDefinitionList } from '@/api/chat';
import { getAgentConfigList } from '@/api/agent';
import ParticleBackground from './components/particleBackground';
import DefaultHome from './components/defaultHome';
import NavigationHome from './components/navigationHome';

const supportsCardDisplay = (process: ProcessDefinitionList) => process.showInCard !== 0;
const NAVIGATION_AGENT_CODE = 'navigation-agent';

const ChatPanel: React.FC<{ panelType: AssistantPanelType }> = ({ panelType }) => {
  const {
    ready,
    isTalking,
    processInfo,
    history,
    conversation,
    setProcessInfo,
    setConversation,
    setHistory,
    setTalking,
    config,
  } = useChatContext();
  const { tenantMcid, hasHistory = true } = config || {};
  const [panelVisible, setPanelVisible] = useState(() => window.parent === window);
  const [panelSession, setPanelSession] = useState(0);
  const [pendingRecommendedQuestion, setPendingRecommendedQuestion] = useState<string>();
  const previousPanelVisibleRef = useRef(panelVisible);

  useEffect(() => {
    const handleVisibilityChange = (event: MessageEvent) => {
      if (event.source !== window.parent || event.data?.type !== 'chatbot-visibility-change') return;
      setPanelVisible(Boolean(event.data.payload?.visible));
    };

    window.addEventListener('message', handleVisibilityChange);
    return () => window.removeEventListener('message', handleVisibilityChange);
  }, []);

  // 动态获取智能体列表
  const { data: processList } = useRequest(
    async () => {
      const authorizedProcesses = await getProcessDefinitionList(tenantMcid!);
      const hasNavigationAgentCode = authorizedProcesses.some(process => process.agentCode === NAVIGATION_AGENT_CODE);
      if (panelType !== 'navigation' || hasNavigationAgentCode) return authorizedProcesses;

      const agentConfigs = await getAgentConfigList();
      const agentCodeById = new Map(agentConfigs.map(agent => [agent.id, agent.agentCode]));
      return authorizedProcesses.map(process => ({
        ...process,
        agentCode: agentCodeById.get(process.agentId ?? Number(process.id)),
      }));
    },
    {
      ready: !!tenantMcid,
      onSuccess: data => {
        const defaultProcess =
          panelType === 'navigation'
            ? data?.find(process => process.agentCode === NAVIGATION_AGENT_CODE)
            : data?.find(supportsCardDisplay);
        if (defaultProcess && !processInfo) {
          setProcessInfo?.(defaultProcess);
        }
      },
    },
  );
  const cardProcessList = useMemo(() => processList?.filter(supportsCardDisplay), [processList]);
  const navigationProcess = useMemo(
    () => processList?.find(process => process.agentCode === NAVIGATION_AGENT_CODE),
    [processList],
  );

  const handleClose = () => {
    window.parent?.postMessage(
      {
        type: 'chatbot-close',
      },
      '*',
    );
  };

  const handleNewConversation = useCallback(() => {
    setConversation?.(undefined);
    setHistory?.(undefined);
    setProcessInfo?.(panelType === 'navigation' ? navigationProcess : cardProcessList?.[0]);
    setTalking?.(false);
    setPendingRecommendedQuestion(undefined);
    setPanelSession(current => current + 1);
  }, [cardProcessList, navigationProcess, panelType, setConversation, setHistory, setProcessInfo, setTalking]);

  useEffect(() => {
    const wasPanelVisible = previousPanelVisibleRef.current;
    const closed = !panelVisible && wasPanelVisible;
    previousPanelVisibleRef.current = panelVisible;

    if (closed) {
      handleNewConversation();
    }
  }, [handleNewConversation, panelVisible]);

  return (
    <div className={`${styles.chatPane} ${panelType === 'navigation' ? styles.navigationPane : ''}`}>
      {ready ? (
        <>
          {hasHistory && <Left active={panelVisible} processList={processList} onNewConversation={handleNewConversation} />}
          <div
            className={`${styles.content} ${isTalking ? styles.contentTalking : ''} ${
              !hasHistory ? styles.contentLargeScreen : ''
            }`}
          >
            {panelType !== 'navigation' && (
              <ParticleBackground key={`particle-${panelSession}`} active={panelVisible && !isTalking} />
            )}
            <div
              className={styles.backgroundImage}
              style={{ backgroundImage: `url(${backgroundImage})` }}
              aria-hidden="true"
            />
            <div className={styles.particleVeil} aria-hidden="true" />
            <div className={styles.gridOverlay} aria-hidden="true" />
            {!hasHistory && isTalking && panelType !== 'navigation' && (
              <button type="button" className={styles.largeScreenNewChat} onClick={handleNewConversation}>
                <PlusOutlined />
                新对话
              </button>
            )}
            {panelType === 'navigation' && !isTalking && (
              <div className={styles.navigationBadge}>
                <span aria-hidden="true">✦</span>
                智能导航
              </div>
            )}
            <div className={styles.closeBtn} onClick={handleClose}>
              <CloseOutlined />
            </div>
            <div className={styles.center}>
              <div
                className={`${isTalking ? styles.chatContent : styles.homeContent} ${
                  panelType === 'navigation' && !isTalking ? styles.navigationHomeContent : ''
                }`}
              >
                {!isTalking &&
                  (panelType === 'navigation' ? (
                    <NavigationHome />
                  ) : (
                    <DefaultHome
                      processInfo={processInfo}
                      processList={cardProcessList}
                      onSelect={process => setProcessInfo?.(process)}
                      onRecommendedQuestionSelect={setPendingRecommendedQuestion}
                    />
                  ))}
                <ChatInterface
                  key={`chat-interface-${panelSession}`}
                  active={panelVisible}
                  title={history?.name || conversation?.name || '新对话'}
                  panelType={panelType}
                  recommendedQuestion={pendingRecommendedQuestion}
                  onRecommendedQuestionConsumed={() => setPendingRecommendedQuestion(undefined)}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center flex-1 self-center">配置加载中...</div>
      )}
    </div>
  );
};

// 主题包装组件：根据 theme 动态配置 Ant Design 主题
const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme: themeMode } = useChatContext();
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}
    >
      {children}
    </ConfigProvider>
  );
};

const ChatPanelPage: React.FC<{ panelType?: AssistantPanelType }> = ({ panelType = 'general' }) => {
  return (
    <ChatProvider panelType={panelType}>
      <ThemeWrapper>
        <ChatPanel panelType={panelType} />
      </ThemeWrapper>
    </ChatProvider>
  );
};

export default ChatPanelPage;
